import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { selectAuth, setActiveDropDownTab, setActiveTab } from '../../redux/features/authSlice';
import { IAuthUser } from '../../redux/features/authSlice/authSlice.types';
import { useGetProfileQuery, useUpdateProfileMutation } from '../../redux/api/profileApi/profileApi';
import jwt_decode from 'jwt-decode';
import CustomLoading from '../../components/CustomLoading';
import { BiSolidBank } from 'react-icons/bi';
import { AiFillSetting } from 'react-icons/ai';
import { TbDeviceMobileDollar } from 'react-icons/tb';
import CustomModal from '../../components/CustomModal';
import CustomInput from '../../components/CustomInput';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

const Wallet = () => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const { token } = useSelector(selectAuth);
    const authData: IAuthUser = token ? jwt_decode(token) : null;
    const { data: profileData, isLoading: profileLoading } = useGetProfileQuery(
        authData?.id
    );
    const [updateProfile, updateResult] = useUpdateProfileMutation();
    const [bankInfo, setBankInfo] = useState([
        {
            title: 'Bank Name',
            value: '',
            iconBg: '#99DD00',
            cardBg: '#D2E7A9'
        },
        {
            title: 'Bank Branch Name',
            value: '',
            iconBg: '#F8B018',
            cardBg: '#F8DB9E'
        },
        {
            title: 'Bank Routing Number',
            value: '',
            iconBg: '#00B3B3',
            cardBg: '#95DCDC'
        },
        {
            title: 'Account Holder Name',
            value: '',
            iconBg: '#FFCE0D',
            cardBg: '#FBE79A'
        },
        {
            title: 'Account Number',
            value: '',
            iconBg: '#0099DD',
            cardBg: '#95D2ED'
        },
    ]);

    const [mobileBankingInfo, setMobileBinkingInfo] = useState([
        {
            title: 'BKash Number',
            value: '',
            iconBg: '#22c55e',
            cardBg: '#A7E8BF'
        },
    ]);

    const [bankShowModal, setBankShowModal] = useState(false);
    const [mobileBankingShowModal, setMobileBankingShowModal] = useState(false);
    const [selectedMobileBanking, setSelectedMobileBanking] = useState('BKash');

    const bankInfoValidationSchema = Yup.object().shape({
        bankname: Yup.string().required('Bank name is required'),
        bankbranchname: Yup.string().required('Bank branch name is required'),
        bankroutingnumber: Yup.string().required('Bank routing number is required'),
        accountholdername: Yup.string().required('Account holder name is required'),
        accountnumber: Yup.string().required('Account number is required'),
    });

    const bankInfoInitialValues = {
        bankname: '',
        bankbranchname: '',
        bankroutingnumber: '',
        accountholdername: '',
        accountnumber: '',
    };

    const bankInfoFormik = useFormik({
        initialValues: bankInfoInitialValues,
        validationSchema: bankInfoValidationSchema,
        onSubmit: async (values) => {
            const data = {
                bank_name: values?.bankname,
                bank_branch: values?.bankbranchname,
                bank_acc_name: values?.accountholdername,
                bank_acc_no: values?.accountnumber,
                bank_routing_no: values?.bankroutingnumber,
            };
            try {
                await updateProfile({ data, profileId: authData?.id }).unwrap();
                toast.success("Bank information added successfully!");
                setBankShowModal(false);
            } catch (error) {
                console.log(error);
                toast.error(error?.data?.message || "Something went wrong!");
            }
        },
    });
    const mobileBankingValidationSchema = Yup.object().shape({
        bkashnumber: Yup.string().required('BKash number is required'),
    });

    const mobileBankingInitialValues = {
        bkashnumber: '',
    };

    const mobileBankingFormik = useFormik({
        initialValues: mobileBankingInitialValues,
        validationSchema: mobileBankingValidationSchema,
        onSubmit: async (values) => {
            const data = {
                bkash_no: values?.bkashnumber,
            };
            try {
                await updateProfile({ data, profileId: authData?.id }).unwrap();
                toast.success("Mobile Banking added successfully!");
                setBankShowModal(false);
            } catch (error) {
                console.log(error);
                toast.error(error?.data?.message || "Something went wrong!");
            }
        },
    });

    useEffect(() => {
        if (profileData?.bank_name) {
            const updatedBankInfo = bankInfo.map(item => {
                if (item.title === 'Bank Name') {
                    return { ...item, value: profileData?.bank_name };
                } else if (item.title === 'Bank Branch Name') {
                    return { ...item, value: profileData?.bank_branch };
                } else if (item.title === 'Bank Routing Number') {
                    return { ...item, value: profileData?.bank_routing_no };
                } else if (item.title === 'Account Holder Name') {
                    return { ...item, value: profileData?.bank_acc_name };
                } else if (item.title === 'Account Number') {
                    return { ...item, value: profileData?.bank_acc_no };
                }
                return item;
            });
            bankInfoFormik.setFieldValue('bankname', profileData?.bank_name);
            bankInfoFormik.setFieldValue('bankbranchname', profileData?.bank_branch);
            bankInfoFormik.setFieldValue('bankroutingnumber', profileData?.bank_routing_no);
            bankInfoFormik.setFieldValue('accountholdername', profileData?.bank_acc_name);
            bankInfoFormik.setFieldValue('accountnumber', profileData?.bank_acc_no);
            bankInfoFormik.setFieldValue('accountnumber', profileData?.bank_acc_no);
            setBankInfo(updatedBankInfo);
        }
        if (profileData?.bkash_no) {
            const updatedBankInfo = mobileBankingInfo.map(item => {
                if (item.title === 'BKash Number') {
                    return { ...item, value: profileData?.bkash_no };
                }
                return item;
            });
            mobileBankingFormik.setFieldValue('bkashnumber', profileData?.bkash_no)
            setMobileBinkingInfo(updatedBankInfo);
        }
    }, [profileData]);

    useEffect(() => {
        if (pathname === "/wallet") {
            dispatch(setActiveTab("Wallet"));
            dispatch(setActiveDropDownTab("Wallet"));
        }
    }, [dispatch, pathname]);

    return (
        <div className="w-full flex flex-col gap-10">
            <div className="bg-white shadow-lg rounded-lg p-4 justify-between flex flex-row gap-10">
                <div className="flex flex-col justify-between w-full gap-4">
                    <div className="flex flex-col md:flex-col xl:flex-row justify-between items-center gap-4">
                        <div className="text-2xl font-bold text-text_1 text-left w-full">Account Details</div>
                        <div className='flex flex-col sm:flex-row w-full gap-4'>
                            <div
                                onClick={() => {
                                    setBankShowModal(true);
                                }}
                                className="w-full py-2 px-3 border border-secondary text-secondary rounded bg-secondary/10 flex items-center justify-center gap-2 cursor-pointer transition-all hover:bg-secondary hover:animate-pulse hover:text-white"
                            >
                                {bankInfo?.[0]?.value ? 'Edit Bank Info' : 'Add Bank Info'}
                            </div>
                            <div
                                onClick={() => {
                                    setMobileBankingShowModal(true);
                                }}
                                className="w-full py-2 px-3 border border-green-500 text-green-500 rounded bg-green-500/10 flex items-center justify-center gap-2 cursor-pointer transition-all hover:bg-green-500 hover:animate-pulse hover:text-white"
                            >
                                {mobileBankingInfo?.[0]?.value ? 'Edit Mobile Banking Info' : 'Add Mobile Banking Info'}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row gap-4 items-center w-full">
                        <div className='text-text_2 italic text-lg'>See Your Account Details</div>
                    </div>
                </div>
                <div className="h-40 hidden lg:flex">
                    <img
                        src="https://res.cloudinary.com/di23omfvx/image/upload/v1692428968/self_driving_car_Road_1_npjom3.gif"
                        alt="self drive"
                        className="h-full w-full"
                    />
                </div>
            </div>
            {bankInfo[0]?.value && <div className="flex flex-col gap-10">
                <div className='flex justify-between items-center bg-white rounded-lg shadow-lg py-2 px-4 flex-col sm:flex-row gap-4 w-full'>
                    <div className='text-lg font-semibold text-secondary text-left w-full'>Bank Information</div>
                    <div
                        onClick={() => {
                            setBankShowModal(true);
                        }}
                        className="w-full sm:max-w-fit py-2 px-3 border border-secondary text-secondary rounded bg-secondary/10 flex items-center justify-center gap-2 cursor-pointer transition-all hover:bg-secondary hover:animate-pulse hover:text-white"
                    >
                        Edit Information
                    </div>
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4'>
                    {bankInfo?.map((bank, index) => {
                        return (
                            <div key={index} className='p-3 rounded-lg flex flex-col gap-4' style={{ backgroundColor: bank?.cardBg }}>
                                <div className='w-fit p-1.5 rounded-full text-white text-3xl flex justify-center items-center' style={{ backgroundColor: bank?.iconBg }}>
                                    <BiSolidBank />
                                </div>
                                <div>
                                    <div className='text-text_1 text-lg font-semibold mb-1'>{bank?.title}</div>
                                    <div className='text-text_1 text-md'>{bank?.value}</div>
                                </div>
                                <div className='flex items-end justify-end'>
                                    <div className='w-fit p-1.5 rounded-md bg-white text-2xl'>
                                        <AiFillSetting color={bank?.iconBg} />
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>}

            {mobileBankingInfo[0]?.value && <div className="flex flex-col gap-10">
                <div className='flex justify-between items-center bg-white rounded-lg shadow-lg py-2 px-4 flex-col sm:flex-row gap-4 w-full'>
                    <div className='text-lg font-semibold text-green-500 text-left w-full'>Mobile Banking Information</div>
                    <div
                        onClick={() => {
                            setMobileBankingShowModal(true);
                        }}
                        className="w-full sm:max-w-fit py-2 px-3 border border-green-500 text-green-500 rounded bg-green-500/10 flex items-center justify-center gap-2 cursor-pointer transition-all hover:bg-green-500 hover:animate-pulse hover:text-white"
                    >
                        Edit Information
                    </div>
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4'>
                    {mobileBankingInfo?.map((mobileBank, index) => {
                        return (
                            <div key={index} className='p-3 rounded-lg flex flex-col gap-4' style={{ backgroundColor: mobileBank?.cardBg }}>
                                <div className='w-fit p-1.5 rounded-full text-white text-3xl flex justify-center items-center' style={{ backgroundColor: mobileBank?.iconBg }}>
                                    <TbDeviceMobileDollar />
                                </div>
                                <div>
                                    <div className='text-text_1 text-lg font-semibold mb-1'>{mobileBank?.title}</div>
                                    <div className='text-text_1 text-md'>{mobileBank?.value}</div>
                                </div>
                                <div className='flex items-end justify-end'>
                                    <div className='w-fit p-1.5 rounded-md bg-white text-2xl'>
                                        <AiFillSetting color={mobileBank?.iconBg} />
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>}

            <CustomModal
                showModal={bankShowModal}
                setShowModal={setBankShowModal}
                submitButton={true}
                handleSubmit={bankInfoFormik.handleSubmit}
                title={bankInfo?.[0]?.value ? "Update Bank Information" : "Add Bank Information"}
            >
                <form
                    onSubmit={bankInfoFormik.handleSubmit}
                    className="w-full flex flex-col gap-2 mt-5 min-w-[70%]"
                >
                    <div className="flex flex-col sm:flex-row gap-5">
                        <CustomInput
                            label="BANK NAME"
                            type="text"
                            setFieldValue={bankInfoFormik.setFieldValue}
                            value={bankInfoFormik.values.bankname}
                            touched={bankInfoFormik.touched.bankname}
                            error={bankInfoFormik.errors.bankname}
                            handleBlur={bankInfoFormik.handleBlur}
                        />
                        <CustomInput
                            label="BANK BRANCH NAME"
                            type="text"
                            setFieldValue={bankInfoFormik.setFieldValue}
                            value={bankInfoFormik.values.bankbranchname}
                            touched={bankInfoFormik.touched.bankbranchname}
                            error={bankInfoFormik.errors.bankbranchname}
                            handleBlur={bankInfoFormik.handleBlur}
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-5">
                        <CustomInput
                            label="BANK ROUTING NUMBER"
                            type="text"
                            setFieldValue={bankInfoFormik.setFieldValue}
                            value={bankInfoFormik.values.bankroutingnumber}
                            touched={bankInfoFormik.touched.bankroutingnumber}
                            error={bankInfoFormik.errors.bankroutingnumber}
                            handleBlur={bankInfoFormik.handleBlur}
                        />
                        <CustomInput
                            label="ACCOUNT HOLDER NAME"
                            type="text"
                            setFieldValue={bankInfoFormik.setFieldValue}
                            value={bankInfoFormik.values.accountholdername}
                            touched={bankInfoFormik.touched.accountholdername}
                            error={bankInfoFormik.errors.accountholdername}
                            handleBlur={bankInfoFormik.handleBlur}
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-5">
                        <CustomInput
                            label="ACCOUNT NUMBER"
                            type="text"
                            setFieldValue={bankInfoFormik.setFieldValue}
                            value={bankInfoFormik.values.accountnumber}
                            touched={bankInfoFormik.touched.accountnumber}
                            error={bankInfoFormik.errors.accountnumber}
                            handleBlur={bankInfoFormik.handleBlur}
                        />
                    </div>
                </form>
            </CustomModal>

            <CustomModal
                showModal={mobileBankingShowModal}
                setShowModal={setMobileBankingShowModal}
                submitButton={true}
                handleSubmit={mobileBankingFormik.handleSubmit}
                title={bankInfo?.[0]?.value ? "Update Bank Information" : "Add Bank Information"}
            >
                <form
                    onSubmit={mobileBankingFormik.handleSubmit}
                    className="w-full flex flex-col gap-2 mt-5 min-w-[70%]"
                >
                    <div className="flex flex-col sm:flex-row gap-5">
                        <div className="w-full mt-2">
                            <div className="text-text_2 text-md font-semibold mb-2">
                                SELECT MOBILE BANKING
                            </div>
                            <select
                                id="mobileBanking"
                                value={selectedMobileBanking}
                                onChange={(e) => setSelectedMobileBanking(e.target.value)}
                                className="w-full border-2 p-2 rounded-lg focus:outline-none focus:border-secondary"
                            >
                                <option value="BKash">
                                    BKash
                                </option>
                            </select>
                        </div>
                        <CustomInput
                            label="BKASH NUMBER"
                            type="text"
                            setFieldValue={mobileBankingFormik.setFieldValue}
                            value={mobileBankingFormik.values.bkashnumber}
                            touched={mobileBankingFormik.touched.bkashnumber}
                            error={mobileBankingFormik.errors.bkashnumber}
                            handleBlur={mobileBankingFormik.handleBlur}
                        />
                    </div>
                </form>
            </CustomModal>

            {(profileLoading || updateResult.isLoading) && <CustomLoading />}
        </div>
    )
}

export default Wallet